/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useQuery } from '@tanstack/react-query';
import type { ProductType } from '../../../../../../types';
import type { GetPresetsResponse } from '../../../../../../api/rater.api';
import { getPresetsByProduct } from '../../../../../../api/rater.api';

import { sortCoverages } from '../utils/customize-view.utils';
import { getPresetSecurityQuestionsByProduct } from '../../utils/primeX.util';
import { PRESET_NAME_MAPPING } from '../../hooks/useGetQuoteOptionPresets';
import type { CustomizeQuoteAccountData } from '../reducer/types';

export interface UseGetPresetsParams {
  queryKey: (string | number | undefined)[];
  accountData: CustomizeQuoteAccountData | undefined;
  productType: ProductType;
  limit: number;
  deductible?: number;
  effectiveDate: string | undefined;
  stateDispatch?: (state: GetPresetsResponse) => void;
  agencyId: string;
  enabled?: boolean;
  isEndorsement?: boolean;
  policyId?: string;
}

export const useGetPresets = ({
  queryKey,
  accountData,
  productType,
  limit,
  deductible,
  effectiveDate,
  agencyId,
  enabled = true,
  stateDispatch,
  isEndorsement = false,
  policyId,
}: UseGetPresetsParams) => {
  const PRESET_NAME =
    PRESET_NAME_MAPPING[productType as keyof typeof PRESET_NAME_MAPPING];

  const presetPayload = {
    industryClass: accountData?.industryClass!,
    accountId: accountData?.id! ?? accountData?.accountId,
    isFranchisee: accountData?.isFranchise!,
    franchiseSystemConnectivity: accountData?.franchiseSystemConnectivity!,
    naicsCode: accountData?.naicsCode!,
    revenue: accountData?.revenue!,
    totalRevenue: accountData?.totalRevenue,
    state: accountData?.state!,
    yearEstablished: accountData?.yearEstablished!,
    selectedLimit: limit,
    selectedDeductible: deductible,
    product: productType,
    preset: PRESET_NAME,
    agencyId,
    effectiveDate,
    ...getPresetSecurityQuestionsByProduct(accountData, productType),
    isEndorsement,
    ...(isEndorsement && { policyId }),
  };

  return useQuery(
    [
      ...queryKey,
      {
        PRESET_NAME,
        productType,
        naics: accountData?.naicsCode,
        presetPayload,
      },
    ],
    () =>
      getPresetsByProduct(
        productType,
        accountData?.naicsCode as number,
        PRESET_NAME,
        {
          data: presetPayload,
        }
      ).then((response) => {
        const sortedResponseData = sortCoverages(response.data);

        stateDispatch?.(sortedResponseData);
        return sortedResponseData;
      }),
    {
      keepPreviousData: true,
      retry: 2,
      enabled:
        !!presetPayload.agencyId &&
        Boolean(accountData) &&
        Boolean(limit) &&
        Boolean(agencyId) &&
        enabled,
    }
  );
};
